import React, { ReactNode, useContext } from 'react'
import CurrentUserResource from 'resources/CurrentUserResource'
import { getIn } from 'components/Formik/forms';
import { useOrbit } from 'providers/OrbitProvider';
import { ClientRecord, ClientRoleRecord, ContractRecord, OrganizationRecord, UserRecord, VendorRecord } from './DataModel';


type ContractWithNames = {
  id: string
  type: string
  clientId: string
  clientName: string
  vendorId: string
  vendorName: string
}

interface CurrentUserProviderBaseProps {
  children?: ReactNode
}

interface CurrentUserProviderResourceProps extends CurrentUserProviderBaseProps {
  currentUserResource: CurrentUserResource
  override?: Record<string,never>
}

interface CurrentUserProviderOverrideProps extends CurrentUserProviderBaseProps {
  currentUserResource?: never
  override: {
    currentUser: UserRecord
    currentOrganization?: OrganizationRecord
    accessibleClients?: ClientRecord[]
    accessibleVendorContracts?: ContractRecord[]
    accessibleClientContracts?: ContractRecord[]
    accessibleVendors?: VendorRecord[]
  }
}

type CurrentUserProviderProps = CurrentUserProviderResourceProps | CurrentUserProviderOverrideProps

const CurrentUserContext = React.createContext<{
  currentUser: UserRecord;
  currentOrganization: OrganizationRecord;
  accessibleClients: ClientRecord[];
  accessibleVendorContracts: ContractRecord[];
  accessibleClientContracts: ContractRecord[];
  accessibleVendors: VendorRecord[];
  accessibleVendorContractsWithNames: ContractWithNames[];
  getClientRole: (clientId?: string) => ClientRoleRecord | null;
}>({
  currentUser: { type: 'user', id: 'unknown', attributes: {} },
  currentOrganization: { type: 'organization', id: 'unknown', attributes: {} },
  accessibleClients: [],
  accessibleVendorContracts: [],
  accessibleClientContracts: [],
  accessibleVendors: [],
  accessibleVendorContractsWithNames: [],
  getClientRole: () => null
});

export default function CurrentUserProvider({ children, currentUserResource, override = {}} : CurrentUserProviderProps) {
  const { store } = useOrbit()

  const currentUser = override.currentUser || (currentUserResource as CurrentUserResource).get()
  const currentOrganization = override.currentOrganization || store.cache.query(q => q.findRelatedRecord(currentUser, 'organization')) || { type: 'organization', id: 'unknown', attributes: {} }
  const accessibleClients = override.accessibleClients || store.cache.query(q => q.findRelatedRecords(currentUser, 'clients')) || []
  const accessibleVendorContracts = override.accessibleVendorContracts || store.cache.query(q => q.findRelatedRecords(currentUser, 'vendorContracts')) || []
  const accessibleClientContracts = override.accessibleClientContracts || store.cache.query(q => q.findRelatedRecords(currentUser, 'clientContracts')) || []
  const accessibleVendors = override.accessibleVendors || store.cache.query(q => q.findRelatedRecords(currentUser, 'vendors')) || []
  const accessibleVendorContractsWithNames = accessibleVendorContracts.map(contract => {
    return {
      id: getIn(contract, 'keys.remoteId'),
      type: 'contract',
      clientId: getIn(contract, 'relationships.client.data.id'),
      clientName: getIn(store.cache.query(q => q.findRelatedRecord(contract, 'client')), 'attributes.name'),
      vendorId: getIn(contract, 'relationships.vendor.data.id'),
      vendorName: getIn(store.cache.query(q => q.findRelatedRecord(contract, 'vendor')), 'attributes.name'),
    }
  })

  const getClientRole = (clientId?: string) => {
    if(!clientId) {
      return null
    }

    const clientRoles = store.cache.query<ClientRoleRecord []>(q => q.findRelatedRecords(currentUser, 'clientRoles'))
    return clientRoles.find(record => getIn(record, 'relationships.client.data.id') === clientId) ?? null
  }

  return (
    <CurrentUserContext.Provider value={{ currentUser, currentOrganization, accessibleClients, accessibleVendorContracts, accessibleClientContracts, accessibleVendors, accessibleVendorContractsWithNames, getClientRole }}>
      {children}
    </CurrentUserContext.Provider>
  )
}

export function useCurrentUser() {
  return useContext(CurrentUserContext)
}
